<template>
  <form class="flex w-full flex-col space-y-4">
    <div class="flex justify-between">
      <div class="flex flex-col">
        <div class="flex items-end space-x-2">
          <Input
            :model-value="milage"
            :label="t('milage')"
            :warning="milageWarning"
            :disabled="disabled"
            :max-fraction-digits="0"
            type="number"
            adornment="km"
            adornment-transparent
            @update:model-value="(value) => emit('update:milage', value ?? null)"
          />
        </div>
      </div>
    </div>
    <div class="w-full">
      <InputLabel :label="fuelLevelLabel" :warning="fuelLevelWarning" />
      <div class="flex items-center space-x-2">
        <input
          :value="props.fuelLevel"
          type="range"
          min="0"
          :disabled="disabled"
          max="1"
          class="slider"
          :step="fuelType === FuelType.ELECTRIC ? 0.01 : 0.125"
          @input="
            (evt: Event) => {
              emit('update:fuelLevel', parseFloat((evt.target as HTMLInputElement).value));
              emit('fuelLevelTouched');
            }
          "
        />
        <div class="flex items-center space-x-2">
          <Input
            :model-value="fuelLevelDisplayValue"
            type="number"
            :disabled="disabled"
            :class="fuelType === FuelType.ELECTRIC ? 'w-12' : 'w-10'"
            :max="fuelType === FuelType.ELECTRIC ? 100 : 8"
            :adornment="fuelLevelAdornment"
            data-testid="fuel-level"
            adornment-transparent
            @input="emit('fuelLevelTouched')"
            @update:model-value="(value) => onFuelLevelDisplayValueChange(Number(value))"
          />
        </div>
      </div>
    </div>
    <div v-if="showTyreConditionFields" class="grid grid-cols-2 gap-4">
      <Select
        :model-value="tyreConditionFront"
        :options="tyreConditionOptions"
        :label="t('tyreConditionFront')"
        :placeholder="t('pleaseMeasure')"
        :warning="!tyreConditionFront ? t('pleaseMeasure') : undefined"
        @update:model-value="(value) => emit('update:tyreConditionFront', value as TyreCondition)"
      />
      <Select
        :model-value="tyreConditionRear"
        :options="tyreConditionOptions"
        :label="t('tyreConditionRear')"
        :placeholder="t('pleaseMeasure')"
        :warning="!tyreConditionRear ? t('pleaseMeasure') : undefined"
        @update:model-value="(value) => emit('update:tyreConditionRear', value as TyreCondition)"
      />
    </div>
  </form>
</template>
<script lang="ts" setup>
import Input from '@/components/Input.vue';
import { FuelType } from '@/entities/cars/fuel-type.enum';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import InputLabel from '../InputLabel.vue';
import Select from '@/components/Select.vue';
import { TyreCondition } from '@carvia/ros-client-types';

const { t } = useI18n();
const props = defineProps<{
  fuelType: FuelType;
  milage: number | null;
  fuelLevel: number | null;
  tyreConditionFront?: TyreCondition;
  tyreConditionRear?: TyreCondition;
  showMilageWarning?: boolean;
  showFuelLevelNotUpdated?: boolean;
  showMilageNotUpdated?: boolean;
  showTyreConditionFields?: boolean;
  disabled?: boolean;
}>();

const milageWarning = computed(() => {
  if (props.showMilageNotUpdated) return t('notUpdated');
  if (props.showMilageWarning) return t('milageWarning');
  return '';
});
const fuelLevelLabel = computed(() =>
  props.fuelType === FuelType.ELECTRIC ? t('battery') : t('fuelLevel'),
);
const fuelLevelWarning = computed(() => (props.showFuelLevelNotUpdated ? t('notUpdated') : ''));
const fuelLevelAdornment = computed(() => (props.fuelType === FuelType.ELECTRIC ? '%' : `/8`));

const fuelLevelDisplayValue = computed(() => {
  return props.fuelType === FuelType.ELECTRIC
    ? Math.round((props.fuelLevel ?? 0) * 100)
    : (props.fuelLevel ?? 0) * 8;
});
const onFuelLevelDisplayValueChange = (value: number) => {
  emit(
    'update:fuelLevel',
    Math.min(props.fuelType === FuelType.ELECTRIC ? value / 100 : value / 8, 1),
  );
};

const tyreConditionOptions = computed(() =>
  Object.values(TyreCondition).map((condition) => ({
    label: t(`tyreCondition.${condition}`),
    value: condition,
  })),
);

const emit = defineEmits<{
  (e: 'update:milage', milage: number | null): void;
  (e: 'update:fuelLevel', fuelLevel: number): void;
  (e: 'update:tyreConditionFront', tyreCondition: TyreCondition): void;
  (e: 'update:tyreConditionRear', tyreCondition: TyreCondition): void;
  (e: 'fuelLevelTouched'): void;
}>();
</script>

<style scoped>
.slider {
  -webkit-appearance: none;
  @apply h-7 w-full appearance-none rounded-full border border-obsidian-500 bg-transparent p-2 transition-all focus:border-primary;
}
.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  @apply h-4 w-4 cursor-pointer rounded-full border border-primary bg-primary transition-all;
}
.slider::-moz-range-thumb {
  @apply h-4 w-4 cursor-pointer rounded-full border border-primary bg-primary transition-all;
}
</style>
<i18n lang="json">
{
  "en": {
    "milage": "Milage",
    "fuelLevel": "Fuel Level",
    "battery": "Battery",
    "notUpdated": "not updated",
    "milageWarning": "check input",
    "tyreConditionFront": "Front tyre tread depth",
    "tyreConditionRear": "Rear tyre tread depth",
    "pleaseMeasure": "Please measure"
  },
  "de": {
    "fuelLevel": "Tankfüllstand",
    "battery": "Ladestand",
    "milage": "Kilometerstand",
    "notUpdated": "nicht aktualisiert",
    "milageWarning": "Eingabe überprüfen",
    "tyreConditionFront": "Vorderreifen Profiltiefe",
    "tyreConditionRear": "Hinterreifen Profiltiefe",
    "pleaseMeasure": "Bitte messen"
  },
  "es": {
    "milage": "Kilometraje",
    "fuelLevel": "Nivel de combustible",
    "battery": "Batería",
    "notUpdated": "sin actualizar",
    "milageWarning": "compruebe el valor",
    "tyreConditionFront": "Profundidad de dibujo neumáticos delanteros",
    "tyreConditionRear": "Profundidad de dibujo neumáticos traseros",
    "pleaseMeasure": "Mida, por favor"
  }
}
</i18n>
